import { useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useBuyerAuth } from '@/context/BuyerAuthContext';
import { apiError } from '@/api/auction';
import { INPUT_CLS, LABEL_CLS } from '@/components/brand/BrandKit';
import { at } from '@/i18n/auctionI18n';

const EMPTY = { full_name: '', company: '', email: '', phone: '', password: '', trade_license: '' };

function AuthField({ label, name, form, set, type = 'text', ph = '' }) {
  return (
    <div>
      <Label className={LABEL_CLS}>{label}</Label>
      <Input data-testid={`buyer-${name}`} type={type} value={form[name]} onChange={(e) => set(name, e.target.value)} placeholder={ph} className={INPUT_CLS} />
    </div>
  );
}

function BuyerAuthFields({ isRegister, form, set, lang }) {
  return (
    <>
      {isRegister && (
        <>
          <AuthField label={at(lang, 'fullName')} name="full_name" form={form} set={set} ph={at(lang, 'namePh')} />
          <AuthField label={at(lang, 'company')} name="company" form={form} set={set} ph={at(lang, 'companyPh')} />
          <AuthField label={at(lang, 'phone')} name="phone" form={form} set={set} ph={at(lang, 'phonePh')} />
          <AuthField label={at(lang, 'tradeLicence')} name="trade_license" form={form} set={set} />
        </>
      )}
      <AuthField label={at(lang, 'emailLabel')} name="email" form={form} set={set} type="email" ph={at(lang, 'emailPh')} />
      <AuthField label={at(lang, 'passwordLabel')} name="password" form={form} set={set} type="password" ph={isRegister ? at(lang, 'passwordPhReg') : ''} />
    </>
  );
}

export function BuyerAuthDialog({ open, onOpenChange, onSuccess, defaultMode = 'register', lang = 'en' }) {
  const { login, register } = useBuyerAuth();
  const [mode, setMode] = useState(defaultMode);
  const [form, setForm] = useState(EMPTY);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const isAr = lang === 'ar';
  const isRegister = mode === 'register';

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      if (isRegister) await register(form);
      else await login({ email: form.email, password: form.password });
      onOpenChange(false);
      setForm(EMPTY);
      onSuccess && onSuccess();
    } catch (e) { setErr(apiError(e)); } finally { setBusy(false); }
  };

  const toggle = () => { setErr(''); setMode(isRegister ? 'login' : 'register'); };
  const submitLabel = busy ? at(lang, 'pleaseWait') : (isRegister ? at(lang, 'createAccount') : at(lang, 'signinTitle'));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir={isAr ? 'rtl' : 'ltr'} className="max-w-md bg-white">
        <DialogTitle className="font-heading font-black text-2xl text-[#121212]">{isRegister ? at(lang, 'registerTitle') : at(lang, 'signinTitle')}</DialogTitle>
        <DialogDescription className="font-body text-sm text-[#666666]">{isRegister ? at(lang, 'registerSub') : at(lang, 'signinSub')}</DialogDescription>
        <div className="mt-4 space-y-4">
          <BuyerAuthFields isRegister={isRegister} form={form} set={set} lang={lang} />
          {err && <p data-testid="buyer-auth-error" role="alert" className="bg-red-50 border border-red-200 text-red-600 font-body text-sm p-3 rounded-lg">{err}</p>}
          <button type="button" data-testid="buyer-auth-submit" onClick={submit} disabled={busy} className="btn-primary w-full disabled:opacity-50">{submitLabel}</button>
          <p className="text-center font-body text-sm text-[#666666]">
            {isRegister ? at(lang, 'alreadyRegistered') : at(lang, 'newBuyer')}{' '}
            <button type="button" data-testid="buyer-auth-toggle" onClick={toggle} className="font-heading font-bold text-[#EE5A01] hover:underline">{isRegister ? at(lang, 'toggleToSignin') : at(lang, 'toggleToRegister')}</button>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
